import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";

function TrainerAiStudio() {
  const user = getStoredUser();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const loginMessage = isArabic ? "سجل الدخول بحساب مدرب أولًا." : "Please log in with a trainer account first.";

  const copy = isArabic
    ? {
        eyebrow: "استوديو الذكاء الاصطناعي",
        title: "أنشئ كورسات وتقييمات ذكية بسهولة",
        intro:
          "هذه الصفحة مخصصة بالكامل لمسار الذكاء الاصطناعي حتى يكون أوضح وأسهل في الاستخدام. اكتب فكرة الكورس، وسيجهز لك أهدافًا وأسئلة وتوصيات، ثم يمكنك إنشاء الحزمة كاملة بضغطة واحدة.",
        leftTitle: "مدخلاتك",
        rightTitle: "المخرجات الذكية",
        highlightOneTitle: "صمم بسرعة",
        highlightOneText: "حوّل فكرة عامة إلى كورس وتقييم وأسئلة خلال ثوانٍ.",
        highlightTwoTitle: "راجع قبل الإنشاء",
        highlightTwoText: "شاهد الأهداف والأسئلة والتوصيات قبل إضافة أي شيء داخل المنصة.",
        highlightThreeTitle: "أنشئ بضغطة واحدة",
        highlightThreeText: "إذا أعجبك المقترح، أنشئ الكورس والتقييم المزدوج والأسئلة فورًا.",
        topic: "موضوع الكورس أو الوحدة",
        topicPlaceholder: "مثال: الأمن السيبراني للمبتدئين",
        level: "مستوى المتعلمين",
        levelPlaceholder: "مثال: مبتدئ أو متوسط",
        notes: "ملاحظات إضافية",
        notesPlaceholder: "مثال: أريد التركيز على حل المشكلات والعمل الجماعي",
        questionCount: "عدد الأسئلة",
        generate: "توليد مقترح ذكي",
        generating: "جار تجهيز المقترح...",
        createPackage: "إنشاء كورس وتقييم كامل",
        generatedDraft: "المقترح الذكي",
        objectives: "الأهداف التعليمية",
        questions: "الأسئلة المقترحة",
        recommendations: "توصيات للمدرب",
        empty: "لم يتم توليد أي مقترح بعد.",
        mode: "وضع التوليد",
        points: "درجة",
        options: "الاختيارات",
        success: "تم تجهيز المقترح الذكي بنجاح",
        packageSuccess: "تم إنشاء الكورس والتقييم والأسئلة من المقترح الذكي",
        packageReadyTitle: "الحزمة الجاهزة",
        packageReadyHint: "احتفظ بهذه الأكواد لاستخدامها مع الطلاب أو لمراجعة التقييمات لاحقًا.",
        courseId: "معرف الكورس",
        sharedCode: "الكود الأساسي",
        accessCodes: "أكواد الدخول",
        preAssessment: "التقييم القبلي",
        postAssessment: "التقييم البعدي",
        preferredTypes: "أنواع الأسئلة المفضلة",
        allowResults: "السماح للطالب برؤية الدرجة",
        allowAnswers: "السماح للطالب بمراجعة الإجابات",
        allowAi: "السماح للطالب برؤية تقرير الذكاء الاصطناعي",
        copyCode: "نسخ الكود",
        copyAllCodes: "نسخ كل الأكواد",
        copiedCode: "تم نسخ الكود بنجاح",
        draftExport: "تصدير المسودة",
        draftCopy: "نسخ المسودة",
        copiedDraft: "تم نسخ المسودة",
        exportedDraft: "تم تصدير المسودة",
        generateError: "تعذر توليد المقترح الذكي",
        packageError: "تعذر إنشاء الحزمة الكاملة",
        topicRequired: "اكتب موضوعًا أولًا",
        providerFallback: "وضع احتياطي ذكي",
        providerOpenai: "OpenAI"
      }
    : {
        eyebrow: "AI Studio",
        title: "Generate smart courses and assessments comfortably",
        intro:
          "This page is dedicated to the AI workflow so it has enough space and clarity. Describe the course idea, get objectives, questions, and recommendations, then create the full package in one step.",
        leftTitle: "Your Input",
        rightTitle: "AI Output",
        highlightOneTitle: "Design Faster",
        highlightOneText: "Turn a rough idea into a course, paired assessment, and questions in seconds.",
        highlightTwoTitle: "Review Before Creation",
        highlightTwoText: "See the objectives, questions, and recommendations before adding anything to the platform.",
        highlightThreeTitle: "Create in One Click",
        highlightThreeText: "If the draft looks right, create the course, paired assessment, and questions immediately.",
        topic: "Course or unit topic",
        topicPlaceholder: "Example: Cybersecurity for beginners",
        level: "Learner level",
        levelPlaceholder: "Example: beginner or intermediate",
        notes: "Extra notes",
        notesPlaceholder: "Example: focus on problem-solving and teamwork",
        questionCount: "Question count",
        generate: "Generate Smart Draft",
        generating: "Preparing the draft...",
        createPackage: "Create Full Course Package",
        generatedDraft: "Smart Draft",
        objectives: "Learning Objectives",
        questions: "Suggested Questions",
        recommendations: "Trainer Recommendations",
        empty: "No draft has been generated yet.",
        mode: "Generation Mode",
        points: "points",
        options: "Options",
        success: "The AI draft is ready",
        packageSuccess: "The full course package was created successfully",
        packageReadyTitle: "Created Package",
        packageReadyHint: "Keep these codes handy for students and for later assessment review.",
        courseId: "Course ID",
        sharedCode: "Base Code",
        accessCodes: "Access Codes",
        preAssessment: "Pre Assessment",
        postAssessment: "Post Assessment",
        preferredTypes: "Preferred Question Types",
        allowResults: "Allow students to see scores",
        allowAnswers: "Allow students to review answers",
        allowAi: "Allow students to see AI reports",
        copyCode: "Copy Code",
        copyAllCodes: "Copy All Codes",
        copiedCode: "Code copied successfully",
        draftExport: "Export Draft",
        draftCopy: "Copy Draft",
        copiedDraft: "Draft copied successfully",
        exportedDraft: "Draft exported successfully",
        generateError: "Unable to generate the AI draft",
        packageError: "Unable to create the full package",
        topicRequired: "Enter a topic first",
        providerFallback: "Smart fallback",
        providerOpenai: "OpenAI"
      };

  const [form, setForm] = useState({
    topic: "",
    audience_level: isArabic ? "مبتدئ" : "beginner",
    notes: "",
    question_count: 6,
    preferred_types: ["mcq", "essay", "rating"],
    show_results_to_student: false,
    show_answers_to_student: false,
    show_ai_report_to_student: false
  });
  const [draft, setDraft] = useState(null);
  const [provider, setProvider] = useState("");
  const [createdPackage, setCreatedPackage] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);

  useEffect(() => {
    if (!feedback) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setFeedback(""), 3200);
    return () => window.clearTimeout(timeoutId);
  }, [feedback]);

  const questionTypeLabel = (type) =>
    ({
      essay: isArabic ? "سؤال مقالي" : "Essay",
      mcq: isArabic ? "اختيار من متعدد" : "Multiple Choice",
      true_false: isArabic ? "صح / خطأ" : "True / False",
      rating: isArabic ? "تقييم رقمي" : "Rating"
    }[type] || type);

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setFeedbackType("success");
      setFeedback(copy.copiedCode);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(isArabic ? "تعذر النسخ في هذا المتصفح" : "Copy failed on this browser");
    }
  };

  const togglePreferredType = (type) => {
    setForm((current) => {
      const hasType = current.preferred_types.includes(type);
      if (hasType && current.preferred_types.length === 1) {
        return current;
      }

      return {
        ...current,
        preferred_types: hasType
          ? current.preferred_types.filter((item) => item !== type)
          : [...current.preferred_types, type]
      };
    });
  };

  const exportTextFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setFeedbackType("success");
    setFeedback(copy.exportedDraft);
  };

  const draftText = draft
    ? [
        draft.course_title,
        "",
        draft.course_description,
        "",
        `${copy.objectives}:`,
        ...(draft.learning_objectives || []),
        "",
        `${copy.questions}:`,
        ...((draft.questions || []).map((question, index) => {
          const options = question.options?.length
            ? ` (${copy.options}: ${question.options.map((option) => option.option_text).join(" / ")})`
            : "";
          return `${index + 1}. ${question.question_text} - ${questionTypeLabel(question.question_type)} - ${question.points} ${copy.points}${options}`;
        })),
        "",
        `${copy.recommendations}:`,
        ...(draft.recommendations || [])
      ].join("\n")
    : "";

  const copyDraft = async () => {
    if (!draftText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(draftText);
      setFeedbackType("success");
      setFeedback(copy.copiedDraft);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(isArabic ? "تعذر النسخ في هذا المتصفح" : "Copy failed on this browser");
    }
  };

  const exportDraft = () => {
    if (!draftText) {
      return;
    }

    exportTextFile(draftText, `ai-draft-${draft?.course_title || "course"}.txt`);
  };

  const copyAllPackageCodes = async () => {
    if (!createdPackage) {
      return;
    }

    const content = [
      `${copy.sharedCode}: ${createdPackage.shared_code}`,
      ...((createdPackage.assessments || []).map((assessment) =>
        `${assessment.type === "pre" ? copy.preAssessment : copy.postAssessment}: ${assessment.access_code}`
      ))
    ].join("\n");

    try {
      await navigator.clipboard.writeText(content);
      setFeedbackType("success");
      setFeedback(copy.copiedCode);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(isArabic ? "تعذر النسخ في هذا المتصفح" : "Copy failed on this browser");
    }
  };

  const generateDraft = async () => {
    if (!user?.id) {
      setFeedbackType("error");
      setFeedback(loginMessage);
      return;
    }

    if (!form.topic.trim()) {
      setFeedbackType("error");
      setFeedback(copy.topicRequired);
      return;
    }

    try {
      setIsGenerating(true);
      setCreatedPackage(null);
      const response = await api.post("/trainer/ai-assistant/generate", {
        trainer_id: user.id,
        topic: form.topic,
        audience_level: form.audience_level,
        notes: form.notes,
        question_count: Number(form.question_count) || 6,
        preferred_types: form.preferred_types,
        language
      });
      setDraft(response.data.assistant_output || null);
      setProvider(response.data.provider || "fallback");
      setFeedbackType("success");
      setFeedback(copy.success);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.generateError);
    } finally {
      setIsGenerating(false);
    }
  };

  const createPackage = async () => {
    if (!user?.id) {
      setFeedbackType("error");
      setFeedback(loginMessage);
      return;
    }

    if (!draft) {
      return;
    }

    try {
      setIsCreatingPackage(true);
      const response = await api.post("/trainer/ai-assistant/create-package", {
        trainer_id: user.id,
        course_title: draft.course_title,
        course_description: draft.course_description,
        assessment_title: draft.assessment_title,
        questions: draft.questions || [],
        show_results_to_student: form.show_results_to_student,
        show_answers_to_student: form.show_answers_to_student,
        show_ai_report_to_student: form.show_ai_report_to_student
      });
      setCreatedPackage(response.data || null);
      setFeedbackType("success");
      setFeedback(copy.packageSuccess);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(error.response?.data?.error || copy.packageError);
    } finally {
      setIsCreatingPackage(false);
    }
  };

  if (!user?.id) {
    return (
      <div className="panel">
        <div className="empty-panel">
          <p>{loginMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stack-lg">
      {feedback && <div className={`feedback-banner feedback-banner--${feedbackType}`}>{feedback}</div>}

      <section className="panel ai-studio-hero">
        <div className="ai-studio-hero__copy">
          <p className="eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="ai-studio-hero__text">{copy.intro}</p>
        </div>
      </section>

      <section className="ai-studio-highlights">
        <div className="dashboard-card ai-highlight-card">
          <h3>{copy.highlightOneTitle}</h3>
          <p>{copy.highlightOneText}</p>
        </div>
        <div className="dashboard-card ai-highlight-card">
          <h3>{copy.highlightTwoTitle}</h3>
          <p>{copy.highlightTwoText}</p>
        </div>
        <div className="dashboard-card ai-highlight-card">
          <h3>{copy.highlightThreeTitle}</h3>
          <p>{copy.highlightThreeText}</p>
        </div>
      </section>

      <section className="ai-studio-grid">
        <div className="panel ai-studio-panel">
          <div className="card-title-row">
            <h3>{copy.leftTitle}</h3>
          </div>

          <div className="stack-lg">
            <div className="field-stack">
              <label className="field-label">{copy.topic}</label>
              <input
                className="text-input"
                placeholder={copy.topicPlaceholder}
                value={form.topic}
                onChange={(e) => setForm((current) => ({ ...current, topic: e.target.value }))}
              />
            </div>

            <div className="field-grid">
              <div className="field-stack">
                <label className="field-label">{copy.level}</label>
                <input
                  className="text-input"
                  placeholder={copy.levelPlaceholder}
                  value={form.audience_level}
                  onChange={(e) => setForm((current) => ({ ...current, audience_level: e.target.value }))}
                />
              </div>

              <div className="field-stack">
                <label className="field-label">{copy.questionCount}</label>
                <input
                  className="text-input"
                  type="number"
                  min="3"
                  max="20"
                  value={form.question_count}
                  onChange={(e) => setForm((current) => ({ ...current, question_count: e.target.value }))}
                />
              </div>
            </div>

            <div className="field-stack">
              <label className="field-label">{copy.notes}</label>
              <textarea
                className="text-area"
                placeholder={copy.notesPlaceholder}
                value={form.notes}
                onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))}
              />
            </div>

            <div className="field-stack">
              <label className="field-label">{copy.preferredTypes}</label>
              <div className="inline-form">
                {["mcq", "essay", "true_false", "rating"].map((type) => (
                  <label key={type} className="option-row">
                    <input
                      type="checkbox"
                      checked={form.preferred_types.includes(type)}
                      onChange={() => togglePreferredType(type)}
                    />
                    {questionTypeLabel(type)}
                  </label>
                ))}
              </div>
            </div>

            <div className="field-stack">
              <label className="field-label">{copy.createPackage}</label>
              <label className="option-row">
                <input
                  type="checkbox"
                  checked={form.show_results_to_student}
                  onChange={(e) => setForm((current) => ({ ...current, show_results_to_student: e.target.checked }))}
                />
                {copy.allowResults}
              </label>
              <label className="option-row">
                <input
                  type="checkbox"
                  checked={form.show_answers_to_student}
                  onChange={(e) => setForm((current) => ({ ...current, show_answers_to_student: e.target.checked }))}
                />
                {copy.allowAnswers}
              </label>
              <label className="option-row">
                <input
                  type="checkbox"
                  checked={form.show_ai_report_to_student}
                  onChange={(e) => setForm((current) => ({ ...current, show_ai_report_to_student: e.target.checked }))}
                />
                {copy.allowAi}
              </label>
            </div>

            <div className="inline-form">
              <button className="primary-button" onClick={generateDraft} disabled={isGenerating}>
                {isGenerating ? copy.generating : copy.generate}
              </button>
              <button className="secondary-button" onClick={createPackage} disabled={!draft || isCreatingPackage}>
                {copy.createPackage}
              </button>
            </div>
          </div>
        </div>

        <div className="panel ai-studio-panel">
          <div className="card-title-row">
            <h3>{copy.rightTitle}</h3>
            {provider && (
              <span className="status-pill">
                {copy.mode}: {provider === "openai" ? copy.providerOpenai : copy.providerFallback}
              </span>
            )}
          </div>

          {draft && (
            <div className="report-box ai-studio-preview-intro">
              <div className="card-title-row">
                <h4>{copy.generatedDraft}</h4>
                <div className="entity-actions">
                  <button className="ghost-button" type="button" onClick={copyDraft}>
                    {copy.draftCopy}
                  </button>
                  <button className="ghost-button" type="button" onClick={exportDraft}>
                    {copy.draftExport}
                  </button>
                </div>
              </div>
              <p>{draft.course_title}</p>
            </div>
          )}

          {!draft && (
            <div className="empty-panel compact-panel">
              <p>{copy.empty}</p>
            </div>
          )}

          {draft && (
            <div className="stack-lg">
              {createdPackage && (
                <div className="dashboard-card dashboard-card--nested">
                  <div className="card-title-row">
                    <h3>{copy.packageReadyTitle}</h3>
                    <button className="ghost-button" type="button" onClick={copyAllPackageCodes}>
                      {copy.copyAllCodes}
                    </button>
                  </div>
                  <p className="helper-text">{copy.packageReadyHint}</p>
                  <div className="details-grid">
                    <div className="detail-chip">
                      <span>{copy.courseId}</span>
                      <strong>{createdPackage.course_id}</strong>
                    </div>
                    <div className="detail-chip">
                      <span>{copy.sharedCode}</span>
                      <strong>{createdPackage.shared_code}</strong>
                      <button className="ghost-button" type="button" onClick={() => copyText(createdPackage.shared_code)}>
                        {copy.copyCode}
                      </button>
                    </div>
                  </div>
                  {!!createdPackage.assessments?.length && (
                    <div className="entity-list">
                      {createdPackage.assessments.map((assessment) => (
                        <div key={assessment.id} className="entity-card entity-card--stack">
                          <div className="entity-main entity-main--static">
                            <strong>
                              {assessment.type === "pre" ? copy.preAssessment : copy.postAssessment}
                            </strong>
                            <span>
                              {copy.accessCodes}: {assessment.access_code}
                            </span>
                            <button className="ghost-button" type="button" onClick={() => copyText(assessment.access_code)}>
                              {copy.copyCode}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="report-box">
                <h4>{draft.course_title}</h4>
                <p>{draft.course_description}</p>
              </div>

              {!!draft.learning_objectives?.length && (
                <div className="dashboard-card dashboard-card--nested">
                  <h3>{copy.objectives}</h3>
                  <ul className="flat-list">
                    {draft.learning_objectives.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {!!draft.questions?.length && (
                <div className="dashboard-card dashboard-card--nested">
                  <h3>{copy.questions}</h3>
                  <div className="entity-list">
                    {draft.questions.map((question, index) => (
                      <div key={`${question.question_text}-${index}`} className="entity-card entity-card--stack">
                        <div className="entity-main entity-main--static">
                          <strong>{question.question_text}</strong>
                          <span>
                            {questionTypeLabel(question.question_type)} - {question.points} {copy.points}
                          </span>
                          {!!question.options?.length && (
                            <span className="helper-text">
                              {copy.options}: {question.options.map((option) => option.option_text).join(" / ")}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!draft.recommendations?.length && (
                <div className="dashboard-card dashboard-card--nested">
                  <h3>{copy.recommendations}</h3>
                  <ul className="flat-list">
                    {draft.recommendations.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default TrainerAiStudio;
